import React, { useState } from 'react';
import cancelBookingDialog from './cancelBookingDialog';
import ConfirmDialog from './ConfirmDialog';
import DeskLegend from './DeskLegend';
import WeekGrid from './WeekGrid';
import { buildWeeks, dayKey, isBookableDate } from '../../../helpers/desk-booking';
import type { ConfirmDialogOptions } from './ConfirmDialog';
import type { WeekGridCell } from './WeekGrid';
import type { BookingIndex } from '../../../helpers/desk-booking';
import type { DeskBooking, DeskBookingRange } from '../../../types';

type Props = {
  index: BookingIndex,
  range: DeskBookingRange,
  holidays: Array<string>,
  today: string,
  mapDate: string,
  pickDate: string | null,
  targetUserId: number,
  targetUserName: string,
  actingForSelf: boolean,
  busy: boolean,
  onShowDate: (date: string) => void,
  onStartPick: (date: string) => void,
  onCancel: (booking: DeskBooking) => void,
};

const MyDays = (props: Props): React.ReactElement => {
  const {
    index, range, holidays, today, mapDate, pickDate, targetUserId, targetUserName, actingForSelf, busy, onShowDate, onStartPick, onCancel,
  } = props;
  const [dialog, setDialog] = useState<ConfirmDialogOptions | null>(null);

  const renderCell = (date: string): WeekGridCell => {
    const booking = index.byUserDate[dayKey(targetUserId, date)];
    if (booking) {
      const desk = index.desksById[booking.deskId];

      return {
        state: 'booked',
        label: desk ? `${desk.label} · ${desk.room}` : 'Booked',
        disabled: false,
        onClick: () => {
          onShowDate(date);
          setDialog(cancelBookingDialog(
            actingForSelf ? 'your' : <><strong>{targetUserName}</strong>'s</>,
            desk?.label || '?',
            date,
            () => onCancel(booking),
          ));
        },
      };
    }
    if (holidays.includes(date)) return { state: 'holiday', label: 'Holiday', disabled: true };
    if (!isBookableDate(date, range, holidays)) return { state: 'disabled', label: '', disabled: true };

    if (date === pickDate) return { state: 'picking', label: 'Pick on map…', disabled: false, onClick: () => onStartPick(date) };

    return { state: 'empty', label: '+ Pick desk', disabled: false, onClick: () => onStartPick(date) };
  };

  return (
    <div className="my-days">
      <WeekGrid weeks={buildWeeks(range)} today={today} mapDate={pickDate ? undefined : mapDate} busy={busy} renderCell={renderCell} />

      <DeskLegend
        items={[
          { swatch: 'mine', label: 'Your desk that day' },
          { swatch: 'empty', label: 'No desk yet' },
          { swatch: 'holiday', label: 'Holiday' },
          { swatch: 'today', label: 'Today' },
          { swatch: 'on-map', label: 'Day shown on the map' },
        ]}
      />
      <small className="text-muted d-block mt-2">Click “+ Pick desk”, then click a free desk on the map to book it. Click a booked day to cancel it.</small>

      {dialog && <ConfirmDialog {...dialog} onClose={() => setDialog(null)} />}
    </div>
  );
};

export default MyDays;
